import { usePage } from "@inertiajs/react"
import type { PageProps } from "@/types/inertia"

// Public pages exist at "/x" (English) and "/es/x" (Spanish). These helpers
// keep visitors on the URLs of their language as they navigate.

// The pairs come from the server in the locale_slugs shared prop — the static
// pages and the condition and therapy rows of the brand being served. There is
// no copy of them here: the two sites share paths (/about is hers and the
// venue's), only some of them are paired, and a page added in the admin has to
// work without a deploy.
type Pairs = Record<string, string>

function englishOf(path: string, rows: Pairs): string | undefined {
  return Object.keys(rows).find((en) => rows[en] === path)
}

export function stripLocale(path: string, rows: Pairs = {}): string {
  const english = englishOf(path, rows)
  if (english) return english
  const stripped = path.replace(/^\/es(?=\/|$)/, "")
  return stripped === "" ? "/" : stripped
}

// The Spanish URL for Spanish. External and absolute URLs pass through.
export function localizePath(path: string, locale: string, rows: Pairs = {}): string {
  if (locale !== "es" || !path.startsWith("/") || path.startsWith("//")) return path
  // A path may carry a fragment (the index page's listed-only anchors); the
  // slug maps are keyed on the path alone.
  const [ base, fragment ] = stripLocale(path, rows).split("#")
  const spanish = rows[base] ?? (base === "/" ? "/es" : `/es${base}`)
  return fragment ? `${spanish}#${fragment}` : spanish
}

export function useLocalePath(): (path: string) => string {
  const { current_locale, locale_slugs } = usePage<PageProps>().props
  return (path: string) => localizePath(path, current_locale, locale_slugs)
}

// Pages that are a Spanish-language destination in their own right, rather
// than the Spanish version of an English page. A link to one is itself the
// offer, so it points at the Spanish URL from both language versions and
// carries Spanish anchor text — the relevance signal an English page can give
// a Spanish one, and the word a Spanish speaker scanning an English page is
// looking for. The Online Therapy page's in-body link deliberately is not one
// of these: it is a sentence of English prose, and it keeps the English
// version internally linked.
const SPANISH_FIRST = new Set(["/therapy-in-spanish"])

export function useSpanishFirstPath(): (path: string) => string | null {
  const { locale_slugs } = usePage<PageProps>().props
  return (path: string) => (SPANISH_FIRST.has(path) ? (locale_slugs[path] ?? null) : null)
}

// What each language calls itself. A toggle labelled "ES" asks the visitor to
// decode an ISO code; one labelled "Español" is recognised at a glance by the
// person who needs it — who, by definition, may not read the rest of the bar.
export const LANGUAGE_NAMES: Record<string, string> = { en: "English", es: "Español" }

export function otherLocale(locale: string): string {
  return locale === "es" ? "en" : "es"
}

// A visitor who has picked a language is never asked about it again — by the
// toggle or by the offer bar. Kept in this browser only: the locale cookie
// cannot stand in for it, because the server writes that on every request
// whether or not anyone chose anything.
const CHOICE_KEY = "language-choice"

export function rememberLanguageChoice(value: string): void {
  try {
    window.localStorage.setItem(CHOICE_KEY, value)
  } catch {
    // Private mode, or a browser refusing site data: the offer reappearing is
    // a smaller problem than a crash on a public page.
  }
}

export function languageChoiceMade(): boolean {
  try {
    return !!window.localStorage.getItem(CHOICE_KEY)
  } catch {
    return false
  }
}

// Where the language toggle should navigate. Switching to English carries
// ?locale=en once so the server updates the locale cookie.
export function localeSwitchTarget(
  locale: string,
  pathname: string,
  rows: Pairs = {},
): { path: string; query: Record<string, string> } {
  const base = stripLocale(pathname, rows)
  return locale === "es"
    ? { path: localizePath(base, "es", rows), query: {} }
    : { path: base, query: { locale: "en" } }
}
